/** Resolve org branding from `company_settings` (Super Admin → Company Profile). */

import { COMPANY } from '../constants/index.js';
import { getAppMode } from '../saas/appMode.js';
import { resolveLoaderStyleFromBranding, type SaasLoaderStyle } from '../saas/lib/loadingStyle.js';
import { SERVICEAPP_BRAND } from '../share/serviceappBrand.js';
import { countryNameFromIso } from './countryFlags.js';

export type CompanySettingsLike = {
  company_name?: string | null;
  trading_name?: string | null;
  logo_url?: string | null;
  tagline?: string | null;
  phone?: string | null;
  email?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
};

export type ResolvedCompanyBranding = {
  /** Short name customers know the business by — trading name when one is saved. */
  companyName: string;
  /** Registered entity name, for invoices and anything with legal weight. */
  legalName: string;
  logoUrl: string | null;
  tagline: string | null;
  phone: string;
  email: string;
  address: string;
  /** SaaS only — org branding `loader_style` (bubbles | pulse | minimal). */
  loaderStyle: SaasLoaderStyle | null;
};

export const TB_DEFAULT_LOGO_WEBP = '/Logo1.webp';
export const TB_DEFAULT_LOGO_PNG = '/Logo1.png';

/** T&B mascot / Logo1.png — treat as “no custom logo” for SaaS tenants. */
export function isToolsAndBubblesStockLogo(url: string | null | undefined): boolean {
  const u = String(url ?? '')
    .trim()
    .toLowerCase();
  if (!u) return true;
  if (/\/logo1\.(png|webp|jpg|jpeg|svg)(\?|#|$)/.test(u)) return true;
  if (u.includes('toolsandbubbles.com/logo') || u.includes('toolsandbubbles.in/logo')) {
    return true;
  }
  return false;
}

/** T&B PWA / OG defaults that must not appear as a tenant’s social image on SaaS. */
export function isToolsAndBubblesStockOgImage(url: string | null | undefined): boolean {
  if (isToolsAndBubblesStockLogo(url)) return true;
  const u = String(url ?? '')
    .trim()
    .toLowerCase();
  if (/\/icon-512\.png(\?|#|$)/.test(u) || /\/icon-192\.png(\?|#|$)/.test(u)) return true;
  if (u.includes('toolsandbubbles.com/icon') || u.includes('toolsandbubbles.in/icon')) return true;
  return false;
}

/** Default og:image / SERP thumbnail when the tenant has not uploaded one. */
export function getDefaultMarketingOgImagePath(): string {
  return isSaasPlatform() ? SERVICEAPP_BRAND.icon512 : '/icon-512.png';
}

/**
 * Configured OG image, else tenant logo, else the platform mark.
 * On SaaS, inherited T&B assets (`/icon-512.png`, Logo1) count as “not set”.
 */
export function resolveDefaultMarketingOgImagePath(
  configured?: string | null,
  options?: { isSaas?: boolean; logoUrl?: string | null },
): string {
  const saas = options?.isSaas ?? isSaasPlatform();
  const configuredTrim = String(configured ?? '').trim();
  if (configuredTrim && !(saas && isToolsAndBubblesStockOgImage(configuredTrim))) {
    return configuredTrim;
  }
  const logo = String(options?.logoUrl ?? '').trim();
  if (logo && !(saas && isToolsAndBubblesStockOgImage(logo))) {
    return logo;
  }
  return saas ? SERVICEAPP_BRAND.icon512 : '/icon-512.png';
}

/** Legacy aliases — use getDefaultLogoUrls() on SaaS. */
export const DEFAULT_LOGO_WEBP = TB_DEFAULT_LOGO_WEBP;
export const DEFAULT_LOGO_PNG = TB_DEFAULT_LOGO_PNG;

export function isSaasPlatform(): boolean {
  return getAppMode() === 'saas-platform';
}

export function getDefaultLogoUrls(): { webp: string; png: string } {
  if (isSaasPlatform()) {
    return { webp: SERVICEAPP_BRAND.logo, png: SERVICEAPP_BRAND.logo };
  }
  return { webp: TB_DEFAULT_LOGO_WEBP, png: TB_DEFAULT_LOGO_PNG };
}

/** Platform fallback when tenant has not uploaded a logo / company profile yet. */
export function getPlatformDefaultBranding(): ResolvedCompanyBranding {
  if (isSaasPlatform()) {
    return {
      companyName: 'ServiceApp',
      legalName: 'ServiceApp',
      logoUrl: SERVICEAPP_BRAND.logo,
      tagline: null,
      phone: '',
      email: 'hello@serviceapp.ai',
      address: '',
      loaderStyle: null,
    };
  }
  return {
    companyName: COMPANY.NAME,
    legalName: COMPANY.LEGAL_NAME,
    logoUrl: TB_DEFAULT_LOGO_PNG,
    tagline: 'Professional Cleaning Solutions',
    phone: COMPANY.PHONE,
    email: COMPANY.EMAIL,
    address: COMPANY.ADDRESS,
    loaderStyle: null,
  };
}

/** Shared public-page / guest document header logo bounds (matches invoice email hero). */
export const PUBLIC_PAGE_LOGO_MAX_HEIGHT_PX = 40;
export const PUBLIC_PAGE_LOGO_MAX_WIDTH_PX = 120;
export const PUBLIC_PAGE_LOGO_IMG_CLASS = 'max-h-10 max-w-[120px] h-auto w-auto object-contain object-left';
export const PUBLIC_PAGE_LOGO_BOX_CLASS =
  'flex h-10 w-[120px] shrink-0 items-center justify-center overflow-hidden';

/** Registered office when Super Admin company profile has no address saved. */
export const DEFAULT_COMPANY_ADDRESS_FALLBACK =
  'WS _VO/135, 1ST FLOOR ,CC, 54, 2593-5 ,BOSE NAGAR , KADAVANTHARA, ERNAKULAM-682020, KERALA';

export function buildCompanyAddress(settings?: CompanySettingsLike | null): string {
  if (!settings) return '';
  return [
    settings.address_line1,
    settings.address_line2,
    settings.city,
    settings.state,
    settings.pincode,
    countryNameFromIso(settings.country),
  ]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .join(', ');
}

/** Address for invoices/PDFs — T&B fallback only on toolsandbubbles.com. */
export function buildCompanyAddressForDocuments(
  settings?: CompanySettingsLike | Record<string, unknown> | null,
): string {
  const built = buildCompanyAddress(settings as CompanySettingsLike);
  if (built) return built;
  if (isSaasPlatform()) return '';
  return DEFAULT_COMPANY_ADDRESS_FALLBACK;
}

function brandingFromOrgJson(branding?: Record<string, unknown> | null): {
  logoUrl: string | null;
  tagline: string | null;
} {
  if (!branding || typeof branding !== 'object') {
    return { logoUrl: null, tagline: null };
  }
  const logo = String(branding.logo_url ?? branding.logoUrl ?? branding.logo ?? '').trim() || null;
  const tagline = String(branding.tagline ?? '').trim() || null;
  return { logoUrl: logo, tagline };
}

function normalizeCompanyNameKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Registry/legal entity names should not replace the public marketing brand on T&B. */
export function isLegalOrRegistryCompanyName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;

  const normalized = normalizeCompanyNameKey(trimmed);
  const legalNormalized = normalizeCompanyNameKey(COMPANY.LEGAL_NAME);
  if (normalized === legalNormalized) return true;

  if (trimmed.length > 40) return true;
  return /\b(opc|pvt|private limited|opc pvt|limited liability|llp)\b/i.test(trimmed);
}

/**
 * Customer-facing company name for headers, AI widget, and marketing copy.
 * A trading name saved in Company Profile always wins; without one, SaaS tenants use
 * their configured name and T&B keeps "Tools & Bubbles" when settings store the legal name.
 */
export function resolvePublicDisplayCompanyName(
  storedName?: string | null,
  options?: { orgName?: string | null; tradingName?: string | null },
): string {
  const defaults = getPlatformDefaultBranding();
  const trading = String(options?.tradingName || '').trim();
  if (trading) return trading;
  const raw = String(storedName || '').trim() || String(options?.orgName || '').trim();

  if (isSaasPlatform()) {
    return raw || defaults.companyName;
  }

  if (!raw || isLegalOrRegistryCompanyName(raw)) {
    return COMPANY.NAME;
  }

  return raw;
}

/** Prefer tenant company_settings; fall back to org name/branding, then platform defaults. */
export function resolveCompanyBranding(
  settings?: CompanySettingsLike | Record<string, unknown> | null,
  options?: {
    orgName?: string | null;
    orgBranding?: Record<string, unknown> | null;
  },
): ResolvedCompanyBranding {
  const defaults = getPlatformDefaultBranding();
  const row = (settings || {}) as CompanySettingsLike;
  const orgExtras = brandingFromOrgJson(options?.orgBranding);

  const companyName = resolvePublicDisplayCompanyName(row.company_name, {
    orgName: options?.orgName,
    tradingName: row.trading_name,
  });
  const legalName =
    String(row.company_name || '').trim() || String(options?.orgName || '').trim() || companyName;
  const rawLogo = String(row.logo_url || '').trim() || orgExtras.logoUrl || '';
  const logoUrl =
    isSaasPlatform() && isToolsAndBubblesStockLogo(rawLogo) ? defaults.logoUrl : rawLogo || defaults.logoUrl;
  const tagline = String(row.tagline || '').trim() || orgExtras.tagline || defaults.tagline;
  const phone = String(row.phone || '').trim() || defaults.phone;
  const email = String(row.email || '').trim() || defaults.email;
  const address = buildCompanyAddress(row) || defaults.address;

  const loaderStyle = isSaasPlatform() ? resolveLoaderStyleFromBranding(options?.orgBranding) : null;

  return {
    companyName,
    legalName,
    logoUrl,
    tagline,
    phone,
    email,
    address,
    loaderStyle,
  };
}
